import { useContext } from 'react';
import { Controller } from 'react-hook-form';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import {
    UserOutlined,
    IdcardOutlined,
    PhoneOutlined,
    MailOutlined,
    HomeOutlined,
    EnvironmentOutlined,
    SaveOutlined,
    CompassOutlined,
} from '@ant-design/icons';

import { IAppUser } from '../../../../interfaces';
import useAppUserForm from './useAppUserForm';
import { ThemeContext } from '../../../../contexts/ThemeContext';

interface IProps {
    action: 'Adicionar' | 'Editar' | 'Visualizar';
    user?: IAppUser;
    onSubmit: (user: IAppUser) => Promise<void>;
}

export default function AppUserForm({ action, user, onSubmit }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const {
        isLoadingCities,
        handleSubmit,
        handleSubmitForm,
        control,
        isSubmiting
    } = useAppUserForm(onSubmit, user);

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? '#94a3b8' : '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    };

    return (
        <Form
            onFinish={handleSubmit(handleSubmitForm)}
            autoComplete='off'
            layout='vertical'
        >
            <div style={{ marginBottom: 18 }}>
                <div style={sectionTitleStyle}>
                    <UserOutlined style={{ color: '#1677ff' }} />
                    <span>Dados Pessoais e Contato</span>
                </div>
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={12} md={8} lg={8}>
                        <Form.Item
                            label="Nome"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="name"
                                control={control}
                                rules={{ required: { value: true, message: 'Por favor, digite o nome do Usuário!' } }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        prefix={<UserOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='Nome completo'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={4}>
                        <Form.Item
                            label="CPF"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="cpf"
                                control={control}
                                rules={{ required: { value: true, message: 'Por favor, digite o CPF!' } }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        prefix={<IdcardOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='CPF'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={4} lg={4}>
                        <Form.Item
                            label="Telefone"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="phone"
                                control={control}
                                rules={{ required: { value: true, message: 'Por favor, digite o telefone!' } }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        prefix={<PhoneOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='Telefone'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={8}>
                        <Form.Item
                            label="Email"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="email"
                                control={control}
                                rules={{
                                    required: { value: false, message: 'Por favor, digite o email!' },
                                    pattern: { value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, message: 'Email inválido' }
                                }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        prefix={<MailOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='Email'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <div style={{ marginBottom: 20 }}>
                <div style={sectionTitleStyle}>
                    <EnvironmentOutlined style={{ color: '#52c41a' }} />
                    <span>Endereço e Municípios</span>
                </div>
                <Row gutter={[12, 12]}>
                    <Col xs={24} sm={8} md={3} lg={3}>
                        <Form.Item
                            label="CEP"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="cep"
                                control={control}
                                rules={{ required: false }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        prefix={<CompassOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='CEP'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={16} md={7} lg={7}>
                        <Form.Item
                            label="Logradouro"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="street"
                                control={control}
                                rules={{ required: false }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        prefix={<HomeOutlined style={{ color: isDark ? '#666' : '#bfbfbf' }} />}
                                        placeholder='Logradouro'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={12} sm={6} md={2} lg={2}>
                        <Form.Item
                            label="Número"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="number"
                                control={control}
                                rules={{ required: false }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        placeholder='Nº'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={12} sm={10} md={4} lg={4}>
                        <Form.Item
                            label="Bairro"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="neighborhood"
                                control={control}
                                rules={{ required: false }}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        disabled
                                        placeholder='Bairro'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={8} md={8} lg={8}>
                        <Form.Item
                            label="Municípios Vinculados"
                            style={{ marginBottom: 0 }}
                        >
                            <Controller
                                name="city_ids"
                                control={control}
                                rules={{ required: { value: true, message: 'Por favor, selecione ao menos um município!' } }}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        mode='multiple'
                                        allowClear
                                        disabled
                                        loading={isLoadingCities}
                                        options={user?.cities.map((city) => ({ value: city.id, label: `${city.city} - ${city.uf}`, key: city.id }))}
                                        placeholder='Municípios'
                                        maxTagCount='responsive'
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={8}>
                        <Form.Item
                            label="Instituição Beneficiada"
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                disabled
                                value={
                                    user?.social_institutions && user.social_institutions.length > 0
                                        ? user.social_institutions.map((si) => si.name).join(', ')
                                        : (user?.social_institution_name || '')
                                }
                                placeholder="Instituição Beneficiada"
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8 }}>
                <Button
                    type='primary'
                    htmlType='submit'
                    icon={<SaveOutlined />}
                    loading={isSubmiting}
                    disabled={isSubmiting || isLoadingCities}
                    style={{
                        borderRadius: 8,
                        height: 38,
                        padding: '0 20px',
                        fontWeight: 600,
                    }}
                >
                    Salvar Alterações
                </Button>
            </div>
        </Form>
    );
}

